import React, { useState, useMemo, useEffect, useRef } from 'react';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import Button from '../../../ui/Button';
import { ICONS } from '../../../ui/icon-map';
import { Field, Input, Select, Checkbox } from '../../../ui/Field/Field';
import { finesService, errorMessage } from '../../../services/finesService';
import {
    VAT_PCT, ADDONS, contractBill, customerOffer, extraDiscountFor, parsePctList, vatBothWays,
} from './discountTax.logic';

const NumberField = ({ label, value, onChange, placeholder = '0.00', suffix, help }) => (
    <Field label={label} help={help}>
        <Input
            type="number"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            suffix={suffix}
            className="tabular-nums"
        />
    </Field>
);

const money = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctText = (n) => `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 5 })}%`;

// tone: the colour of the result value (danger / success / info)
const TONE_TEXT = { danger: 'text-red-700', success: 'text-green-700', info: 'text-blue-700' };

const ResultBox = ({ label, value, tone = 'info', suffix = '', sublabel = '' }) => {
    const [copied, setCopied] = useState(false);
    const empty = value === '' || value === null || value === undefined;

    const handleCopy = () => {
        if (empty) return;
        navigator.clipboard.writeText(String(value));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            title="Click to copy"
            className={`w-full text-center rounded-lg border px-4 py-3 transition-colors ${copied ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
        >
            <span className="flex items-center justify-center gap-2 text-xs font-medium text-gray-600">
                {label}
                <span className={`inline-flex items-center gap-1 ${copied ? 'text-green-700' : 'text-gray-500'}`} aria-live="polite">
                    <Icon glyph={copied ? ICONS.check : ICONS.copy} size="xs" />
                    {copied ? 'Copied' : 'Copy'}
                </span>
            </span>
            <span className={`block mt-1 text-2xl font-semibold tabular-nums ${TONE_TEXT[tone] || 'text-gray-900'}`}>
                {empty ? '-' : `${value}${suffix}`}
            </span>
            {sublabel && <span className="block text-xs text-gray-600">{sublabel}</span>}
        </button>
    );
};

const TH = 'border border-gray-300 px-2 py-1.5 font-semibold';
const TD = 'border border-gray-300 px-2 py-2';
const BILL_HEADS = ['Item', 'Qty x Price', 'Total', 'Discount', 'After discount', `VAT ${VAT_PCT}%`, 'Net (incl. VAT)'];

/** Ejar's calculation, a line per item (Trip Days + add-ons) and the contract's total, as the contract shows it. */
const BillTable = ({ bill, emptyText }) => (
    <table className="w-full border-collapse text-sm tabular-nums text-center">
        <thead>
            <tr className="bg-gray-800 text-white">
                {BILL_HEADS.map((h) => <th key={h} className={TH}>{h}</th>)}
            </tr>
        </thead>
        <tbody>
            {bill ? bill.lines.map((l) => (
                <tr key={l.key} className="text-gray-900">
                    <td className={`${TD} font-medium`}>{l.label}</td>
                    <td className={`${TD} whitespace-nowrap`}>{`${l.qty} x ${money(l.unit)}`}</td>
                    <td className={TD}>{money(l.total)}</td>
                    <td className={TD}>
                        {l.discount ? (
                            <>
                                {money(l.discount)}
                                <span className="block text-xs text-gray-600">{pctText(l.pct)}</span>
                            </>
                        ) : '-'}
                    </td>
                    <td className={TD}>{money(l.afterDiscount)}</td>
                    <td className={TD}>{money(l.vat)}</td>
                    <td className={`${TD} font-semibold`}>{money(l.net)}</td>
                </tr>
            )) : (
                <tr className="text-gray-600"><td className={TD} colSpan={BILL_HEADS.length}>{emptyText}</td></tr>
            )}
        </tbody>
        {bill && (
            <tfoot>
                <tr className="bg-gray-100 font-semibold text-gray-900">
                    <td className={TD} colSpan={2}>Contract total</td>
                    <td className={TD}>{money(bill.total)}</td>
                    <td className={TD}>{bill.discount ? money(bill.discount) : '-'}</td>
                    <td className={TD}>{money(bill.total - bill.discount)}</td>
                    <td className={TD}>{money(bill.vat)}</td>
                    <td className={`${TD} text-blue-700`}>{money(bill.net)}</td>
                </tr>
            </tfoot>
        )}
    </table>
);

const QUOTE_WAIT_MS = 600;      // Ejar is asked once typing stops
const errorText = (err, fallback) => errorMessage(err) || fallback;

const DiscountTaxCalculator = () => {
    // what the user picks: the car model + year (Ejar prices each year apart), the days, the customer's identity (an
    // old customer: Ejar's membership)
    const [models, setModels] = useState([]);
    const [modelsError, setModelsError] = useState('');
    const [car, setCar] = useState('');              // "<model>|<year>"
    const [plate, setPlate] = useState('');          // a plate instead: that very car (owner, 18 Oct 2026)
    const [days, setDays] = useState('');
    const [identity, setIdentity] = useState('');
    // Ejar's answer for them: the rent, the add-on prices, the membership
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState('');
    const [reading, setReading] = useState(false);
    const asked = useRef(0);
    // the add-ons ticked, a price typed where Ejar has none, the two discounts
    const [chosen, setChosen] = useState({});
    const [typedPrices, setTypedPrices] = useState({});
    const [discount, setDiscount] = useState('');
    const [extraDiscount, setExtraDiscount] = useState('');
    // the amount taken from the customer -> the extra discount to type on Ejar (owner, 18 Oct 2026)
    const [paid, setPaid] = useState('');

    const [taxAmount, setTaxAmount] = useState('');

    useEffect(() => {
        let live = true;
        finesService.priceQuoteModels()
            .then((r) => { if (live) setModels((r && r.items) || []); })
            .catch((err) => { if (live) setModelsError(errorText(err, 'The cars could not be read')); });
        return () => { live = false; };
    }, []);

    const dayCount = Math.floor(Number(days));
    const id = identity.trim();
    const [model, year = ''] = car ? car.split('|') : ['', ''];
    const plateText = plate.trim();
    const daysOk = dayCount >= 1 && dayCount <= 365;
    // the car is found before the days are typed (owner, 18 Oct 2026): Ejar is asked for one day until then, and the
    // bill waits for the days
    const askDays = daysOk ? dayCount : 1;
    useEffect(() => {
        const n = asked.current + 1;
        asked.current = n;
        if (!(model || plateText) || (String(days).trim() !== '' && !daysOk)) {
            setQuote(null);
            setQuoteError('');
            setReading(false);
            return undefined;
        }
        setReading(true);
        const timer = setTimeout(() => {
            finesService.priceQuote(plateText ? { plate: plateText, days: askDays, identity: id }
                : { model, year, days: askDays, identity: id })
                .then((q) => { if (asked.current === n) { setQuote(q); setQuoteError(''); } })
                .catch((err) => { if (asked.current === n) { setQuote(null); setQuoteError(errorText(err, 'Ejar did not answer')); } })
                .finally(() => { if (asked.current === n) setReading(false); });
        }, QUOTE_WAIT_MS);
        return () => clearTimeout(timer);
    }, [model, year, plateText, askDays, days, daysOk, id]);

    const offer = customerOffer(quote && quote.customer);
    const offers = useMemo(() => [offer.pct, ...parsePctList(discount)], [offer.pct, discount]);
    const staff = useMemo(() => parsePctList(extraDiscount), [extraDiscount]);
    const prices = useMemo(() => {
        const out = {};
        ADDONS.forEach(({ key }) => {
            const fromEjar = quote && quote.addons ? quote.addons[key] : null;
            out[key] = fromEjar || Number(typedPrices[key]) || null;
        });
        return out;
    }, [quote, typedPrices]);
    const bill = useMemo(
        () => (quote && quote.dailyRate && !reading && daysOk
            ? contractBill({ rate: quote.dailyRate, days: quote.days, offers, staff, membershipPct: offer.membershipPct, chosen, prices })
            : null),
        [quote, reading, daysOk, offers, staff, offer.membershipPct, chosen, prices],
    );
    const reach = useMemo(
        () => (quote && quote.dailyRate && !reading && daysOk && String(paid).trim() !== ''
            ? extraDiscountFor({ rate: quote.dailyRate, days: quote.days, offers, membershipPct: offer.membershipPct, chosen, prices, target: paid })
            : null),
        [quote, reading, daysOk, paid, offers, offer.membershipPct, chosen, prices],
    );
    const tax = useMemo(() => vatBothWays(taxAmount), [taxAmount]);
    let reachSub = 'Type the amount taken from the customer';
    if (reach && reach.kind === 'exact') reachSub = `After the customer's own discount - Ejar will show ${money(reach.bill.net)} AED`;
    if (reach && reach.kind === 'closest') reachSub = `Ejar's rounding cannot land on it exactly - closest ${money(reach.bill.net)} AED`;
    if (reach && reach.kind === 'above') reachSub = `More than the bill: the customer's discount alone makes it ${money(reach.bill.net)} AED`;
    if (reach && reach.kind === 'tooLow') reachSub = 'Too low: the add-ons alone cost that much (the extra discount is on the rent only)';
    const reachPct = reach && (reach.kind === 'exact' || reach.kind === 'closest') ? reach.staffPct : '';

    const modelOptions = [
        { value: '', label: models.length ? 'Choose a car model' : 'Loading the cars...' },
        ...models.map((m) => ({
            value: `${m.model}|${m.year || ''}`,
            label: `${m.make ? `${m.make} ` : ''}${m.model}${m.year ? ` - ${m.year}` : ''}`,
        })),
    ];
    // a plate shows its own car's model + year in the model box (owner, 18 Oct 2026); a car whose model is not in the
    // list (not a rental car any more) gets its own row
    const plateCar = plateText && quote && quote.plate ? `${quote.model}|${quote.year || ''}` : '';
    if (plateCar && !modelOptions.some((o) => o.value === plateCar)) {
        modelOptions.push({ value: plateCar, label: `${quote.make ? `${quote.make} ` : ''}${quote.model}${quote.year ? ` - ${quote.year}` : ''}` });
    }
    let rentText = 'Ejar rent: choose the car (or type its plate) and the days';
    if (reading) rentText = 'Reading Ejar...';
    else if (quote && quote.dailyRate) {
        const which = quote.plate ? `${quote.plate}: ${quote.make ? `${quote.make} ` : ''}${quote.model}${quote.year ? ` - ${quote.year}` : ''}, ` : '';
        rentText = daysOk ? `${which}Ejar rent: ${money(quote.dailyRate)} AED a day (${quote.period})`
            : `${which}Ejar rent: ${money(quote.dailyRate)} AED a day - type the days (a week or a month costs less a day)`;
    }
    else if (quote) rentText = 'Ejar has no price for this car';
    let customerText = 'No ID: a new customer (5%)';
    if (id && quote && quote.customer) {
        if (offer.unread) customerText = 'Ejar did not answer for the membership - try again';
        else if (quote.customer.level) customerText = `${offer.label} (${offer.pct}%)`;
        else if (quote.customer.found) customerText = 'No membership on Ejar: a new customer (5%)';
        else customerText = 'Not a Yelo customer yet: a new customer (5%)';
    }

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {/* Ejar price calculator */}
            <Card
                className="xl:col-span-2"
                title={<span className="inline-flex items-center gap-2"><Icon glyph={ICONS.calculator} size="sm" tone="primary" />Ejar price calculator</span>}
                subtitle="Choose the car and the days: the rent, the add-ons and the membership come from Ejar"
            >
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                        <Field className="lg:col-span-2" label="Car model" error={modelsError || undefined} help={plateText ? 'The plate decides the car' : undefined}>
                            <Select value={plateText ? plateCar : car} onChange={(e) => setCar(e.target.value)} options={modelOptions} disabled={Boolean(plateText)} />
                        </Field>
                        <Field label="Or plate number" help="The number is enough">
                            <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="e.g. 42796" autoComplete="off" />
                        </Field>
                        <NumberField label="Days" value={days} onChange={setDays} placeholder="0" suffix="days" />
                        <Field className="lg:col-span-2" label="Customer ID (old customer)" help="Blank or no membership = a new customer">
                            <Input value={identity} onChange={(e) => setIdentity(e.target.value)} placeholder="784-...." autoComplete="off" />
                        </Field>
                    </div>

                    <div className="flex flex-wrap justify-center gap-3 text-sm" aria-live="polite">
                        <span className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 font-medium text-gray-900">{rentText}</span>
                        <span className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 font-medium text-gray-900">{customerText}</span>
                    </div>
                    {quoteError && <p className="text-center text-sm font-medium text-red-700">{quoteError}</p>}

                    <div className="border-t border-gray-200 pt-4 flex flex-col gap-3">
                        <span className="text-sm font-semibold text-gray-800">Add-ons (Ejar&apos;s prices before VAT)</span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            {ADDONS.map(({ key, label, perDay }) => {
                                const ejarPrice = quote && quote.addons ? quote.addons[key] : null;
                                const unit = perDay ? 'a day' : 'once - airport contracts only';
                                const title = `${label}${ejarPrice ? ` (${money(ejarPrice)} ${unit})` : ''}`;
                                return (
                                    <div key={key} className="flex flex-wrap items-end gap-2">
                                        <Checkbox
                                            label={title}
                                            checked={Boolean(chosen[key])}
                                            onChange={(e) => setChosen((c) => ({ ...c, [key]: e.target.checked }))}
                                        />
                                        {quote && !ejarPrice && (
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                size="sm"
                                                value={typedPrices[key] || ''}
                                                onChange={(e) => setTypedPrices((p) => ({ ...p, [key]: e.target.value }))}
                                                placeholder={`No Ejar price: AED ${perDay ? 'a day' : 'once'}`}
                                                aria-label={`${label} price`}
                                                className="tabular-nums w-48"
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Discount" help="The other offers on the contract, e.g. online payment 10, Summer 20">
                            <Input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" suffix="%" className="tabular-nums" />
                        </Field>
                        <Field label="Extra discount" help="The staff discount typed on Ejar (rent only)">
                            <Input value={extraDiscount} onChange={(e) => setExtraDiscount(e.target.value)} placeholder="0" suffix="%" className="tabular-nums" />
                        </Field>
                    </div>

                    <div className="border-t border-gray-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                        <NumberField
                            label="Amount the customer pays (incl. VAT)"
                            value={paid}
                            onChange={setPaid}
                            suffix="AED"
                            help="Everything: rent + add-ons"
                        />
                        <div className="flex flex-col gap-2">
                            <ResultBox
                                label="Extra discount to type on Ejar"
                                value={reachPct}
                                suffix="%"
                                tone={reach && reach.kind === 'exact' ? 'danger' : 'info'}
                                sublabel={reachSub}
                            />
                            {reachPct !== '' && (
                                <Button size="sm" variant="secondary" onClick={() => setExtraDiscount(String(reachPct))}>
                                    Put it in Extra discount
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <BillTable bill={bill} emptyText={reading ? 'Reading Ejar...' : quote && !daysOk ? 'Type the days' : 'Choose the car and the days'} />
                    </div>
                </div>
            </Card>

            {/* Tax calculator */}
            <Card
                title={<span className="inline-flex items-center gap-2"><Icon glyph={ICONS.invoices} size="sm" tone="primary" />Tax calculator</span>}
                subtitle={`VAT ${VAT_PCT}%, rounded as Ejar rounds`}
            >
                <div className="flex flex-col gap-4">
                    <NumberField label="Enter amount" value={taxAmount} onChange={setTaxAmount} suffix="AED" />
                    <ResultBox
                        label="Price before VAT"
                        value={tax ? tax.before.toFixed(2) : ''}
                        suffix=" AED"
                        tone="success"
                        sublabel={tax ? `If the amount includes VAT (VAT inside: ${money(tax.vatInside)})` : 'If the amount includes VAT'}
                    />
                    <ResultBox
                        label="Price after VAT"
                        value={tax ? tax.after.toFixed(2) : ''}
                        suffix=" AED"
                        tone="info"
                        sublabel={tax ? `If the amount is before VAT (VAT: ${money(tax.vat)})` : 'If the amount is before VAT'}
                    />
                </div>
            </Card>
        </div>
    );
};

export default DiscountTaxCalculator;
